import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Upload, Car, ArrowLeft, Receipt, Fuel, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { VehicleCSVImport } from "@/components/VehicleCSVImport";
import { DKVInvoiceUpload } from "@/components/DKVInvoiceUpload";

interface Vehicle {
  id: string;
  name: string;
  manufacturer: string | null;
  vehicle_type: string | null;
  vin: string | null;
  license_plate: string | null;
  tuv_until: string | null;
  leased_until: string | null;
  responsible_employee_id: string | null;
  dkv_card: string | null;
  year_of_manufacture: number | null;
  first_registration: string | null;
  leasing_rate: number;
  insurance_monthly: number;
  tax_yearly: number;
  fuel_monthly: number;
  ownership: string | null;
  winter_tires: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

interface OneTimeCost {
  id: string;
  vehicle_id: string;
  cost_type: string;
  amount: number;
  cost_date: string;
  description: string | null;
  created_by: string;
  created_at: string;
}

interface Employee {
  id: string;
  name: string;
}

const COST_TYPE_OPTIONS = [
  { value: "treibstoff", label: "Treibstoff" },
  { value: "strom", label: "Strom (Laden)" },
  { value: "waschstrasse", label: "Waschstraße" },
  { value: "reifenwechsel", label: "Reifenwechsel" },
  { value: "werkstatt", label: "Werkstattbesuch" },
  { value: "tuev", label: "TÜV" },
  { value: "reparatur", label: "Reparatur" },
  { value: "inspektion", label: "Inspektion" },
  { value: "sonstiges", label: "Sonstiges" },
];

export function VehicleSettings() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [oneTimeCosts, setOneTimeCosts] = useState<OneTimeCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedVehicleForCosts, setSelectedVehicleForCosts] = useState<Vehicle | null>(null);
  const [formData, setFormData] = useState<Partial<Vehicle>>({
    leasing_rate: 0,
    insurance_monthly: 0,
    tax_yearly: 0,
    winter_tires: false,
  });
  const [costFormData, setCostFormData] = useState({
    cost_type: "",
    amount: 0,
    cost_date: new Date().toISOString().split("T")[0],
    description: "",
  });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [vehiclesRes, employeesRes, costsRes] = await Promise.all([
        supabase.from("vehicles").select("*").order("name"),
        supabase.from("employees").select("id, name").is("archived_at", null).order("name"),
        supabase.from("vehicle_one_time_costs").select("*").order("cost_date", { ascending: false }),
      ]);

      if (vehiclesRes.error) throw vehiclesRes.error;
      if (employeesRes.error) throw employeesRes.error;
      if (costsRes.error) throw costsRes.error;

      setVehicles(vehiclesRes.data || []);
      setEmployees(employeesRes.data || []);
      setOneTimeCosts(costsRes.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      leasing_rate: 0,
      insurance_monthly: 0,
      tax_yearly: 0,
      winter_tires: false,
    });
    setDialogOpen(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingId(vehicle.id);
    setFormData(vehicle);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Bitte geben Sie einen Namen ein");
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        name: formData.name,
        manufacturer: formData.manufacturer || null,
        vehicle_type: formData.vehicle_type || null,
        vin: formData.vin || null,
        license_plate: formData.license_plate || null,
        tuv_until: formData.tuv_until || null,
        leased_until: formData.leased_until || null,
        responsible_employee_id: formData.responsible_employee_id || null,
        dkv_card: formData.dkv_card || null,
        year_of_manufacture: formData.year_of_manufacture || null,
        first_registration: formData.first_registration || null,
        leasing_rate: formData.leasing_rate || 0,
        insurance_monthly: formData.insurance_monthly || 0,
        tax_yearly: formData.tax_yearly || 0,
        ownership: formData.ownership || null,
        winter_tires: formData.winter_tires || false,
        notes: formData.notes || null,
      };

      if (editingId) {
        const { error } = await supabase
          .from("vehicles")
          .update(dataToSave)
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Fahrzeug erfolgreich aktualisiert");
      } else {
        const { error } = await supabase
          .from("vehicles")
          .insert([{ ...dataToSave, created_by: user!.id }]);

        if (error) throw error;
        toast.success("Fahrzeug erfolgreich hinzugefügt");
      }

      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving vehicle:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Möchten Sie dieses Fahrzeug wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("vehicles").delete().eq("id", id);

      if (error) throw error;

      toast.success("Fahrzeug gelöscht");
      loadData();
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleOpenCostDialog = (vehicle: Vehicle) => {
    setSelectedVehicleForCosts(vehicle);
    setCostFormData({
      cost_type: "",
      amount: 0,
      cost_date: new Date().toISOString().split("T")[0],
      description: "",
    });
    setCostDialogOpen(true);
  };

  const handleAddCost = async () => {
    if (!costFormData.cost_type || !costFormData.amount || !selectedVehicleForCosts) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("vehicle_one_time_costs").insert([{
        vehicle_id: selectedVehicleForCosts.id,
        cost_type: costFormData.cost_type,
        amount: costFormData.amount,
        cost_date: costFormData.cost_date,
        description: costFormData.description || null,
        created_by: user!.id,
      }]);

      if (error) throw error;

      toast.success("Kosten erfolgreich hinzugefügt");
      setCostDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error adding cost:", error);
      toast.error("Fehler beim Hinzufügen");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCost = async (costId: string) => {
    if (!confirm("Möchten Sie diesen Kosteneintrag wirklich löschen?")) return;

    try {
      const { error } = await supabase.from("vehicle_one_time_costs").delete().eq("id", costId);

      if (error) throw error;

      toast.success("Kosteneintrag gelöscht");
      loadData();
    } catch (error) {
      console.error("Error deleting cost:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const getTotalMonthlyCost = (vehicle: Vehicle) => {
    return (
      (vehicle.leasing_rate || 0) +
      (vehicle.insurance_monthly || 0) +
      ((vehicle.tax_yearly || 0) / 12)
    );
  };

  // Get fuel/electricity costs grouped by month for a vehicle
  const getFuelCostsByMonth = (vehicleId: string) => {
    const fuelCosts = oneTimeCosts.filter(
      c => c.vehicle_id === vehicleId && (c.cost_type === 'treibstoff' || c.cost_type === 'strom')
    );
    const byMonth: Record<string, number> = {};
    fuelCosts.forEach(c => {
      const key = c.cost_date.substring(0, 7); // YYYY-MM
      byMonth[key] = (byMonth[key] || 0) + (c.amount || 0);
    });
    // Sort descending
    return Object.entries(byMonth)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, total]) => ({ month, total }));
  };

  // Get "sonstige" costs (non fuel/strom) grouped by month
  const getOtherCostsByMonth = (vehicleId: string) => {
    const costs = oneTimeCosts.filter(
      c => c.vehicle_id === vehicleId && c.cost_type !== 'treibstoff' && c.cost_type !== 'strom'
    );
    const byMonth: Record<string, number> = {};
    costs.forEach(c => {
      const key = c.cost_date.substring(0, 7);
      byMonth[key] = (byMonth[key] || 0) + (c.amount || 0);
    });
    return Object.entries(byMonth)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, total]) => ({ month, total }));
  };

  const getLatestMonthValue = (monthlyData: { month: string; total: number }[]) => {
    return monthlyData.length > 0 ? monthlyData[0].total : 0;
  };

  const getCumulativeTotal = (monthlyData: { month: string; total: number }[]) => {
    return monthlyData.reduce((sum, m) => sum + m.total, 0);
  };

  const formatMonthLabel = (monthStr: string) => {
    const [y, m] = monthStr.split("-");
    const d = new Date(parseInt(y), parseInt(m) - 1, 1);
    return format(d, "MMM yyyy", { locale: de });
  };

  const MonthlyBreakdownPopover = ({ data, label }: { data: { month: string; total: number }[]; label: string }) => {
    if (data.length <= 1) return null;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-5 px-1 ml-1">
            <ChevronDown className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="end">
          <p className="text-sm font-semibold mb-2">{label} pro Monat</p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.map(({ month, total }) => (
              <div key={month} className="flex justify-between text-sm">
                <span>{formatMonthLabel(month)}</span>
                <span className="font-medium">{total.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
              </div>
            ))}
          </div>
          <div className="border-t mt-2 pt-2 flex justify-between text-sm font-semibold">
            <span>Gesamt</span>
            <span>{getCumulativeTotal(data).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  const getFuelAndElectricityCostsTotal = (vehicleId: string) => {
    return oneTimeCosts
      .filter(c => c.vehicle_id === vehicleId && (c.cost_type === 'treibstoff' || c.cost_type === 'strom'))
      .reduce((sum, c) => sum + (c.amount || 0), 0);
  };

  const getVehicleOneTimeCostsTotal = (vehicleId: string) => {
    return oneTimeCosts
      .filter(c => c.vehicle_id === vehicleId)
      .reduce((sum, c) => sum + (c.amount || 0), 0);
  };

  const getResponsibleEmployeeName = (employeeId: string | null) => {
    if (!employeeId) return "-";
    const employee = employees.find((e) => e.id === employeeId);
    return employee?.name || "-";
  };

  const getCostTypeLabel = (costType: string) => {
    const option = COST_TYPE_OPTIONS.find(o => o.value === costType);
    return option?.label || costType;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (showImport) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => setShowImport(false)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zur Fahrzeugliste
        </Button>
        <VehicleCSVImport onImportComplete={() => {
          loadData();
          setShowImport(false);
        }} />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5" />
                Fahrzeugverwaltung
              </CardTitle>
              <CardDescription>
                Verwalten Sie alle Firmenfahrzeuge mit ihren Kosten
              </CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <DKVInvoiceUpload 
                vehicles={vehicles.map(v => ({ id: v.id, name: v.name, license_plate: v.license_plate }))} 
                onImportComplete={loadData} 
              />
              <Button variant="outline" onClick={() => setShowImport(true)}>
                <Upload className="mr-2 h-4 w-4" />
                CSV-Import
              </Button>
              <Button onClick={handleAdd}>
                <Plus className="mr-2 h-4 w-4" />
                Fahrzeug hinzufügen
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {vehicles.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Kennzeichen</TableHead>
                  <TableHead>Hersteller</TableHead>
                  <TableHead>Verantwortlicher</TableHead>
                  <TableHead>TÜV bis</TableHead>
                  <TableHead className="text-right">Leasing</TableHead>
                  <TableHead className="text-right">Versicherung</TableHead>
                  <TableHead className="text-right">Steuer/Monat</TableHead>
                  <TableHead className="text-right">Sprit/Tank</TableHead>
                  <TableHead className="text-right">Sonstige</TableHead>
                  <TableHead className="text-right">Monatl. Gesamt</TableHead>
                  <TableHead className="text-right">Kumuliert/Jahr</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vehicles.map((vehicle) => {
                  const fixedMonthly = getTotalMonthlyCost(vehicle);
                  const fuelByMonth = getFuelCostsByMonth(vehicle.id);
                  const otherByMonth = getOtherCostsByMonth(vehicle.id);
                  const latestFuel = getLatestMonthValue(fuelByMonth);
                  const latestOther = getLatestMonthValue(otherByMonth);
                  const totalMonthly = fixedMonthly + latestFuel + latestOther;
                  const cumulFuel = getCumulativeTotal(fuelByMonth);
                  const cumulOther = getCumulativeTotal(otherByMonth);
                  const cumulYearly = (fixedMonthly * 12) + cumulFuel + cumulOther;
                  const eur = (v: number) => v.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

                  return (
                    <TableRow key={vehicle.id}>
                      <TableCell className="font-medium">{vehicle.name}</TableCell>
                      <TableCell>{vehicle.license_plate || "-"}</TableCell>
                      <TableCell>{vehicle.manufacturer || "-"}</TableCell>
                      <TableCell>
                        {getResponsibleEmployeeName(vehicle.responsible_employee_id)}
                      </TableCell>
                      <TableCell>
                        {vehicle.tuv_until ? (
                          <Badge
                            variant={
                              new Date(vehicle.tuv_until) < new Date()
                                ? "destructive"
                                : new Date(vehicle.tuv_until) <
                                  new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
                                ? "secondary"
                                : "outline"
                            }
                          >
                            {format(new Date(vehicle.tuv_until), "dd.MM.yyyy", { locale: de })}
                          </Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right">{eur(vehicle.leasing_rate || 0)}</TableCell>
                      <TableCell className="text-right">{eur(vehicle.insurance_monthly || 0)}</TableCell>
                      <TableCell className="text-right">{eur((vehicle.tax_yearly || 0) / 12)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end">
                          <span>{latestFuel > 0 ? eur(latestFuel) : "-"}</span>
                          <MonthlyBreakdownPopover data={fuelByMonth} label="Sprit/Tankkosten" />
                        </div>
                        {fuelByMonth.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {formatMonthLabel(fuelByMonth[0].month)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end">
                          <span>{latestOther > 0 ? eur(latestOther) : "-"}</span>
                          <MonthlyBreakdownPopover data={otherByMonth} label="Sonstige Kosten" />
                        </div>
                        {otherByMonth.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {formatMonthLabel(otherByMonth[0].month)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{eur(totalMonthly)}</TableCell>
                      <TableCell className="text-right font-semibold">{eur(cumulYearly)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenCostDialog(vehicle)}
                            title="Einmalige Kosten hinzufügen"
                          >
                            <Receipt className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(vehicle)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(vehicle.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Noch keine Fahrzeuge vorhanden
            </p>
          )}
        </CardContent>
      </Card>

      {/* Einmalige Kosten Übersicht */}
      {oneTimeCosts.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Einmalige Kosten (letzte Einträge)
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Fahrzeug</TableHead>
                  <TableHead>Art</TableHead>
                  <TableHead>Beschreibung</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {oneTimeCosts.slice(0, 20).map((cost) => {
                  const vehicle = vehicles.find(v => v.id === cost.vehicle_id);
                  return (
                    <TableRow key={cost.id}>
                      <TableCell>
                        {format(new Date(cost.cost_date), "dd.MM.yyyy", { locale: de })}
                      </TableCell>
                      <TableCell>{vehicle?.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{getCostTypeLabel(cost.cost_type)}</Badge>
                      </TableCell>
                      <TableCell>{cost.description || "-"}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {cost.amount.toLocaleString("de-DE", {
                          style: "currency",
                          currency: "EUR",
                        })}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteCost(cost.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Vehicle Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Firmenfahrzeug 1"
                />
              </div>
              <div>
                <Label>Hersteller</Label>
                <Input
                  value={formData.manufacturer || ""}
                  onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                  placeholder="z.B. VW, BMW, Mercedes"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Fahrzeugart</Label>
                <Select
                  value={formData.vehicle_type || ""}
                  onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pkw">PKW</SelectItem>
                    <SelectItem value="lkw">LKW</SelectItem>
                    <SelectItem value="transporter">Transporter</SelectItem>
                    <SelectItem value="motorrad">Motorrad</SelectItem>
                    <SelectItem value="anhaenger">Anhänger</SelectItem>
                    <SelectItem value="sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>FIN (Fahrzeug-Identifizierungsnummer)</Label>
                <Input
                  value={formData.vin || ""}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                  placeholder="WVWZZZ..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Kennzeichen</Label>
                <Input
                  value={formData.license_plate || ""}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                  placeholder="z.B. B-AB 1234"
                />
              </div>
              <div>
                <Label>DKV Karte</Label>
                <Input
                  value={formData.dkv_card || ""}
                  onChange={(e) => setFormData({ ...formData, dkv_card: e.target.value })}
                  placeholder="DKV-Kartennummer"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>TÜV bis</Label>
                <Input
                  type="date"
                  value={formData.tuv_until || ""}
                  onChange={(e) => setFormData({ ...formData, tuv_until: e.target.value })}
                />
              </div>
              <div>
                <Label>Geleast bis</Label>
                <Input
                  type="date"
                  value={formData.leased_until || ""}
                  onChange={(e) => setFormData({ ...formData, leased_until: e.target.value })}
                />
              </div>
              <div>
                <Label>Erstzulassung</Label>
                <Input
                  type="date"
                  value={formData.first_registration || ""}
                  onChange={(e) => setFormData({ ...formData, first_registration: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Baujahr</Label>
                <Input
                  type="number"
                  value={formData.year_of_manufacture || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, year_of_manufacture: parseInt(e.target.value) || null })
                  }
                  placeholder="z.B. 2023"
                />
              </div>
              <div>
                <Label>Verantwortlicher Mitarbeiter</Label>
                <Select
                  value={formData.responsible_employee_id || "none"}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      responsible_employee_id: value === "none" ? null : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Kein Verantwortlicher</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Besitz</Label>
                <Select
                  value={formData.ownership || ""}
                  onValueChange={(value) => setFormData({ ...formData, ownership: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eigentum">Eigentum</SelectItem>
                    <SelectItem value="leasing">Leasing</SelectItem>
                    <SelectItem value="finanzierung">Finanzierung</SelectItem>
                    <SelectItem value="miete">Miete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="winter_tires"
                  checked={formData.winter_tires || false}
                  onCheckedChange={(checked) => setFormData({ ...formData, winter_tires: checked })}
                />
                <Label htmlFor="winter_tires">Winterreifen vorhanden</Label>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Kosten (€)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Leasing Rate (mtl.)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.leasing_rate || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, leasing_rate: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>
                <div>
                  <Label>Versicherung (mtl.)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.insurance_monthly || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, insurance_monthly: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>
                <div>
                  <Label>Steuer (jährlich)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.tax_yearly || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, tax_yearly: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Treibstoff, Strom, Werkstatt, TÜV und andere einmalige Kosten können über den Quittungs-Button in der Tabelle hinzugefügt werden.
              </p>
            </div>

            <div>
              <Label>Notizen</Label>
              <Textarea
                rows={2}
                value={formData.notes || ""}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add One-Time Cost Dialog */}
      <Dialog open={costDialogOpen} onOpenChange={setCostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Einmalige Kosten für {selectedVehicleForCosts?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div>
              <Label>Art der Kosten *</Label>
              <Select
                value={costFormData.cost_type}
                onValueChange={(value) => setCostFormData({ ...costFormData, cost_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {COST_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Betrag (€) *</Label>
              <Input
                type="number"
                step="0.01"
                value={costFormData.amount || ""}
                onChange={(e) =>
                  setCostFormData({ ...costFormData, amount: parseFloat(e.target.value) || 0 })
                }
              />
            </div>

            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                value={costFormData.cost_date}
                onChange={(e) => setCostFormData({ ...costFormData, cost_date: e.target.value })}
              />
            </div>

            <div>
              <Label>Beschreibung</Label>
              <Textarea
                rows={2}
                value={costFormData.description}
                onChange={(e) => setCostFormData({ ...costFormData, description: e.target.value })}
                placeholder="z.B. Ölwechsel, Bremsbeläge gewechselt..."
              />
            </div>

            {/* Bestehende Kosten für dieses Fahrzeug */}
            {selectedVehicleForCosts && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold mb-2">Bisherige Kosten für dieses Fahrzeug</h4>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {oneTimeCosts
                    .filter(c => c.vehicle_id === selectedVehicleForCosts.id)
                    .slice(0, 10)
                    .map((cost) => (
                      <div key={cost.id} className="flex justify-between text-sm p-2 bg-muted rounded">
                        <span>
                          {format(new Date(cost.cost_date), "dd.MM.yy", { locale: de })} - {getCostTypeLabel(cost.cost_type)}
                        </span>
                        <span className="font-semibold">
                          {cost.amount.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                        </span>
                      </div>
                    ))}
                  {oneTimeCosts.filter(c => c.vehicle_id === selectedVehicleForCosts.id).length === 0 && (
                    <p className="text-sm text-muted-foreground">Noch keine Kosten erfasst</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCostDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAddCost} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Kosten hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
